import { useCandidateRejectionMessagesQuery } from '@/modules/candidates/queries/useCandidateRejectionMessagesQuery';
import { Label } from '@/modules/ui/components/label/Label';
import { Skeleton } from '@/modules/ui/components/skeleton/Skeleton';
import { Textarea } from '@/modules/ui/components/textarea/Textarea';

interface RejectionMessageEditorProps {
  jobId: string;
  candidateId: string;
  firstName: string;
  /** The edited text, or null while the default message is untouched. */
  message: string | null;
  onMessageChange: (message: string) => void;
}

/**
 * The rejection message a single rejection sends. It can be edited but not
 * skipped: candidates who finished prescreening are always told the outcome.
 */
export function RejectionMessageEditor({
  jobId,
  candidateId,
  firstName,
  message,
  onMessageChange,
}: RejectionMessageEditorProps) {
  const recipientsQuery = useCandidateRejectionMessagesQuery({
    jobId,
    candidateIds: [candidateId],
  });

  if (recipientsQuery.isPending) {
    return <Skeleton className="h-28 w-full" />;
  }

  if (recipientsQuery.isError) {
    return (
      <p className="text-muted-foreground text-xs">
        The rejection message could not be loaded. If {firstName} finished
        pre-screening, the default message will still be sent.
      </p>
    );
  }

  const recipient = recipientsQuery.data[0];
  if (!recipient) {
    return (
      <p className="text-muted-foreground text-xs">
        {firstName} will not be sent a rejection message: they have not finished
        pre-screening or there is no LinkedIn conversation with them.
      </p>
    );
  }

  const isEmpty = message !== null && message.trim() === '';

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor="rejection-message">Message to {firstName}</Label>
      <Textarea
        id="rejection-message"
        aria-invalid={isEmpty}
        rows={5}
        value={message ?? recipient.content}
        onChange={(event) => onMessageChange(event.target.value)}
      />
      {isEmpty ? (
        <p className="text-destructive text-xs">The message cannot be empty.</p>
      ) : (
        <p className="text-muted-foreground text-xs">
          Sent on LinkedIn when you reject, so {firstName} is not left without
          an answer. You can edit it before it goes out.
        </p>
      )}
    </div>
  );
}
